import { cookies } from '@/util/cookies'

class IframeAuthService {
  constructor () {
    this.token = null
    this.config = {}
    this.listeners = []
    this.authFailedListeners = []
    this.parentOrigin = null // BS-GO
    this.messageHandler = null
    this.refreshPromise = null
    this.refreshReject = null // Store reject function to call on AUTH_FAILED
    this.sessionExpired = false
  }

  init () {
    if (this.messageHandler) {
      return
    }

    // Listen for messages from parent (BS-GO)
    this.messageHandler = (event) => {
      this.parentOrigin = event.origin

      if (event.data?.type === 'AUTH_TOKEN') {
        this.handleTokenUpdate(event.data.payload, 'postMessage')
      }

      if (event.data?.type === 'AUTH_FAILED') {
        this.handleAuthFailed(event.data.payload)
      }
    }

    window.addEventListener('message', this.messageHandler)

    // Signal to parent (BS-GO) that iframe is ready
    this.signalReady()
  }

  signalReady () {
    // Try parent, if in iframe
    if (window.parent !== window) {
      window.parent.postMessage({ type: 'IFRAME_READY' }, '*')
    }
  }

  handleTokenUpdate (payload, source = 'unknown') {
    const { token, accountId, userId, uid, apiKey, baseUrl } = payload || {}

    this.token = token || null
    this.config = {
      accountId: accountId || null,
      userId: userId || null,
      uid: uid || null,
      apiKey: apiKey || null,
      baseUrl: baseUrl || null,
    }

    // Also update cookies for backward compatibility and backend auth
    if (token) {
      cookies.set('token', token)
    }
    if (accountId) {
      cookies.set('account', accountId)
    }
    if (userId) {
      cookies.set('user', userId)
    }
    if (baseUrl) {
      cookies.set('baseUrl', baseUrl)
    }
    if (apiKey) {
      cookies.set('apikey', apiKey)
    }

    // Notify listeners (e.g., for UI updates)
    this.listeners.forEach(cb => {
      try {
        cb(this.token, this.config)
      } catch (error) {
        console.error('Error in token update listener:', error)
      }
    })

    console.log(`Token updated via ${source}`)
  }

  getToken () {
    return this.token || cookies.get('token')
  }

  getConfig () {
    return this.config
  }

  requestFreshToken () {
    // If session is already known to be expired, reject immediately
    if (this.sessionExpired) {
      return Promise.reject(new Error('Session expired'))
    }

    if (this.refreshPromise) {
      return this.refreshPromise
    }

    this.refreshPromise = new Promise((resolve, reject) => {
      // Store reject function so AUTH_FAILED can call it
      this.refreshReject = reject

      const timeout = setTimeout(() => {
        this.refreshPromise = null
        this.refreshReject = null
        reject(new Error('Token refresh timeout'))
      }, 5000)

      const unsubscribe = this.onTokenUpdate(() => {
        clearTimeout(timeout)
        unsubscribe()
        this.refreshPromise = null
        this.refreshReject = null
        resolve(this.token)
      })

      window.parent.postMessage({ type: 'TOKEN_EXPIRED' }, '*')
    })

    return this.refreshPromise
  }

  handleAuthFailed (payload) {
    const reason = payload?.reason || 'unknown'
    console.error(`Authentication failed: ${reason}`)

    this.sessionExpired = true
    this.token = null

    if (this.refreshReject) {
      this.refreshReject(new Error(`Session expired: ${reason}`))
      this.refreshPromise = null
      this.refreshReject = null
    }

    // Notify auth failed listeners
    this.authFailedListeners.forEach(cb => {
      try {
        cb(reason)
      } catch (error) {
        console.error('Error in auth failed listener:', error)
      }
    })
  }

  onAuthFailed (callback) {
    if (typeof callback !== 'function') {
      console.warn('onAuthFailed requires a function')
      return () => {}
    }

    this.authFailedListeners.push(callback)

    return () => {
      this.authFailedListeners = this.authFailedListeners.filter(cb => cb !== callback)
    }
  }

  isSessionExpired () {
    return this.sessionExpired
  }

  onTokenUpdate (callback) {
    if (typeof callback !== 'function') {
      console.warn('onTokenUpdate requires a function')
      return () => {}
    }

    this.listeners.push(callback)

    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback)
    }
  }

  destroy () {
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler)
      this.messageHandler = null
    }
    this.listeners = []
    this.authFailedListeners = []
    this.token = null
    this.config = {}
    this.refreshPromise = null
    this.refreshReject = null
    this.sessionExpired = false
  }
}

export const iframeAuth = new IframeAuthService()
